<template>
  <div class="min-w-0">
    <div class="rounded-2xl p-[1px] bg-gradient-to-r from-[#213E52]/80 via-[#090D1A]/80 to-[#213E52]/80 shadow-xl shadow-black/30 backdrop-blur-xl min-w-0">
      <div class="rounded-2xl bg-gradient-to-b from-[#101832]/70 to-[#080C19]/80 p-3 sm:p-4 overflow-hidden min-w-0 glass-panel"
           style="font-family: Inter, ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial">
        <div class="flex items-center justify-between mb-3 sm:mb-4 min-w-0 gap-2">
          <div class="bg-gradient-to-r from-[#B8DAFF] to-[#1A6ECA] bg-clip-text text-transparent font-semibold text-base sm:text-lg shrink-0">
            My Positions
          </div>
          <div v-if="hasStake" class="flex items-center gap-2 min-w-0 shrink">
            <div v-if="!canUnstake" class="text-xs text-slate-400 whitespace-nowrap hidden sm:block">
              <span class="text-white/40">Locked for:</span>
              <span class="text-slate-200 font-semibold ml-1">{{ lockHuman }}</span>
            </div>
            <button
              @click="unstakeAll"
              :disabled="!canUnstake"
              :class="['btn-primary shrink-0', !canUnstake ? 'btn-disabled' : '']"
              title="Unstake all (available after lock expires)">
              Unstake
            </button>
          </div>
        </div>

        <div v-if="!hasStake" class="py-8 sm:py-12 grid place-items-center text-center">
          <div>
            <div class="bg-gradient-to-r from-[#B8DAFF] to-[#1A6ECA] bg-clip-text text-transparent text-lg font-semibold">
              No Active Position
            </div>
            <div class="text-white/50 mt-2 text-sm">Stake VRX to start earning rewards</div>
          </div>
        </div>

        <div v-else class="overflow-x-auto -mx-3 sm:-mx-4 px-3 sm:px-4">
          <div class="min-w-[800px]">
            <!-- Table Header -->
            <div class="grid grid-cols-7 gap-2 text-xs text-slate-400 px-3 py-2 mb-2">
              <div>Staked VRX</div>
              <div>Lock Period</div>
              <div>Start Date</div>
              <div>End Date</div>
              <div>Progress</div>
              <div class="text-sky-400">Key Multiplier</div>
              <div class="text-sky-400">Total Shares</div>
            </div>

            <!-- Table Row -->
            <div class="position-row">
              <div class="text-[#B8DAFF] font-semibold tabular-nums flex items-center gap-2">
                <img :src="iconVRX" alt="VRX" class="h-5 w-5 aspect-square object-contain shrink-0" />
                <span class="truncate">{{ fmt18(stakedVRX) }}</span>
              </div>

              <div class="text-slate-200 text-sm">
                <span class="font-semibold">{{ lockLenDays }}d</span>
                <span class="text-sky-400 ml-1.5">(× {{ lockMult }})</span>
              </div>

              <div class="text-slate-300 text-xs tabular-nums">{{ fmtDMY(lockStartTs) }}</div>
              <div class="text-slate-300 text-xs tabular-nums">{{ fmtDMY(lockEndTs) }}</div>

              <div class="flex items-center gap-2">
                <div class="flex-1 relative h-2 rounded-full overflow-hidden"
                     style="background: linear-gradient(135deg, rgba(15, 23, 42, 0.6), rgba(30, 41, 59, 0.5)); border: 1px solid rgba(148, 163, 184, 0.2);">
                  <div class="absolute left-0 top-0 h-full bg-gradient-to-r from-sky-500 to-sky-400 transition-[width] duration-500"
                       :style="{ width: lockProgressPct + '%' }"></div>
                </div>
                <div class="text-slate-400 text-xs tabular-nums shrink-0 w-10 text-right">{{ Math.round(lockProgressPct) }}%</div>
              </div>

              <div class="flex justify-center">
                <span class="multiplier-badge">
                  × {{ keysMultiplier }}
                </span>
              </div>

              <div class="text-[#B8DAFF] font-bold text-sm tabular-nums"># {{ mySharesRowDisplay }}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import iconVRX from '../assets/VRX.png'
import { ethers } from 'ethers'

defineProps({
  hasStake: Boolean,
  canUnstake: Boolean,
  unstakeAll: Function,

  stakedVRX: [String, Number, Object],
  lockLenDays: [String, Number, Object],
  lockMult: [String, Number, Object],
  lockStartTs: [String, Number, Object],
  lockEndTs: [String, Number, Object],
  lockProgressPct: [String, Number, Object],
  keysMultiplier: [String, Number, Object],
  mySharesRowDisplay: [String, Number, Object],
  lockHuman: [String, Number, Object],
  fmtDMY: Function,
})

const fmt18 = (x) => Number(ethers.formatUnits(x ?? 0n, 18)).toLocaleString()
</script>

<style scoped>
/* Glass Panel Effect */
.glass-panel {
  backdrop-filter: blur(20px) saturate(180%);
  -webkit-backdrop-filter: blur(20px) saturate(180%);
  border: 1px solid rgba(255, 255, 255, 0.08);
}

.position-row {
  @apply grid grid-cols-7 gap-2 items-center px-3 py-3 rounded-xl transition-all duration-200;
  background: linear-gradient(135deg, rgba(56, 189, 248, 0.08), rgba(59, 130, 246, 0.05));
  border: 1.5px solid rgba(56, 189, 248, 0.2);
  box-shadow: 
    inset 0 1px 1px 0 rgba(255, 255, 255, 0.08),
    0 2px 12px rgba(0, 0, 0, 0.2);
}

.position-row:hover {
  border-color: rgba(56, 189, 248, 0.3);
  background: linear-gradient(135deg, rgba(56, 189, 248, 0.12), rgba(59, 130, 246, 0.08));
  box-shadow: 
    inset 0 1px 1px 0 rgba(255, 255, 255, 0.1),
    0 4px 16px rgba(56, 189, 248, 0.15);
}

.multiplier-badge {
  @apply inline-flex items-center rounded-lg px-2.5 py-1 text-xs font-bold tabular-nums;
  background: linear-gradient(135deg, rgba(56, 189, 248, 0.2), rgba(59, 130, 246, 0.15));
  border: 1px solid rgba(56, 189, 248, 0.4);
  color: rgb(186, 230, 253);
  box-shadow: 
    inset 0 1px 1px 0 rgba(255, 255, 255, 0.15),
    0 2px 8px rgba(56, 189, 248, 0.2);
}

.btn-disabled {
  @apply opacity-50 cursor-not-allowed;
  pointer-events: none;
  filter: grayscale(0.5);
}
</style>
